import React, { Component } from 'react'
import { AlternativeTemplate, CollageLayoutImageType, CollageLayoutType, Template as TemplateType } from "app/types/model-types"
import { RandomProduct } from "shape-edit/types"
import AlternativeImageSvg from "./alternative-image-svg";
import { canBeOutpaintedOver, getAltImageFromRandomProduct, getTemplateSize } from 'shape-edit/forms/helpers';
import { renderClipMaskShape } from 'template-edit/components/helper';
import { ClipMaskObject, EmptyClipMaskObject } from 'shape-edit/forms/edit-clip-mask-modal';
import { RENDERED_TYPE_BGREMOVE, RENDERED_TYPE_OUTPAINTING } from 'products/types';
import { DeltaPositionsType } from 'template-edit/containers/image-preview';
import { LevelsModeType } from 'template-edit/components/save-template-form';

const { abs, sin, cos, PI, max, min, round, tan, atan2, atan, sqrt, floor, pow } = Math

interface Props {
    randomProduct: RandomProduct
    isAlt: boolean
    levels?: (string | number)[]
    levelsMode?: LevelsModeType
    form: TemplateType | AlternativeTemplate
    collageLayout: CollageLayoutType
    editSourceImage?: Function
    isDraggable: boolean
    isTemplatePreview: boolean
    transX: number
    transY: number
    deltaAngle?: number
    deltaPositions?: DeltaPositionsType
    setDeltaAngle?: Function
    setDeltaPositions?: Function
}

interface State {
    scale: number
}

class SourceImageSvgElements extends Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            scale: 1,
        }
    }

    componentDidMount() {
        this.setScale()
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.form) {
            if (
                (this.props.form.size?.width !== prevProps.form.size?.width) ||
                (this.props.form.size?.height !== prevProps.form.size?.height) ||
                (this.props.form.is_keep_size !== prevProps.form.is_keep_size) ||
                (this.props.randomProduct && (
                    this.props.randomProduct.image_link !== prevProps.randomProduct.image_link ||
                    this.props.randomProduct.bgremovedImage !== prevProps.randomProduct.bgremovedImage ||
                    this.props.randomProduct.activeLevelFromZero !== prevProps.randomProduct.activeLevelFromZero ||
                    this.props.randomProduct.imageBlob?.length !== prevProps.randomProduct.imageBlob?.length) ||
                    this.imageBlobCorrespondsToRequest(this.props.form, this.props.randomProduct) !== this.imageBlobCorrespondsToRequest(prevProps.form, prevProps.randomProduct)
                ) ||
                (this.props.form.flip !== prevProps.form.flip) ||
                (this.props.form.flop !== prevProps.form.flop) ||
                (this.props.form.enlarge !== prevProps.form.enlarge) ||
                (this.props.form.rotation !== prevProps.form.rotation) ||
                (this.props.form.crop !== prevProps.form.crop) ||
                (this.props.form.enlarge_by !== prevProps.form.enlarge_by) ||
                (this.props.form.outpaint_over !== prevProps.form.outpaint_over) ||
                (this.props.form.overscan !== prevProps.form.overscan) ||
                (this.props.form.translate_x !== prevProps.form.translate_x) ||
                (this.props.form.translate_y !== prevProps.form.translate_y) ||
                (this.props.form.is_autocrop !== prevProps.form.is_autocrop) ||
                (this.props.form.max_image_width !== prevProps.form.max_image_width) ||
                (this.props.form.max_image_height !== prevProps.form.max_image_height)
            ) {
                this.setScale()
            }
        }
    }

    setScale = (): void => {
        const { form, randomProduct } = this.props
        const { rotation, enlarge, enlarge_by, overscan, max_image_width, max_image_height } = form

        const r = this.getImageUrlAndSize()

        const imageWidth = (rotation % 180 === 0) ? r.randomProductWidth : r.randomProductHeight
        const imageHeight = (rotation % 180 === 0) ? r.randomProductHeight : r.randomProductWidth

        let scale = 1

        if (imageWidth > 0 && imageHeight > 0) {
            const templateSize = getTemplateSize(form)

            const screenWidth = templateSize.width
            const screenHeight = templateSize.height

            const imageBlobCorrespondsToRequest = this.imageBlobCorrespondsToRequest(form, randomProduct)
            const isOutpaintingOver = this.isOutpaintOver(imageBlobCorrespondsToRequest)

            const byOverscan = !(max_image_width && max_image_height)

            const marginsX = isOutpaintingOver ? 0 : (byOverscan ? 2 * overscan : (max_image_width - screenWidth))
            const marginsY = isOutpaintingOver ? 0 : (byOverscan ? 2 * overscan : (max_image_height - screenHeight))

            const scaleByWidthWithOverscan = (screenWidth + marginsX) / imageWidth
            const scaleByHeightWithOverscan = (screenHeight + marginsY) / imageHeight

            if (enlarge === 1) {
                switch (enlarge_by) {
                    case "bigger":
                        scale = (imageWidth > imageHeight) ? scaleByWidthWithOverscan : scaleByHeightWithOverscan
                        break
                    case "smaller":
                        scale = (imageWidth > imageHeight) ? scaleByHeightWithOverscan : scaleByWidthWithOverscan
                        break
                    case "fit":
                        scale = (scaleByWidthWithOverscan <= scaleByHeightWithOverscan) ? scaleByWidthWithOverscan : scaleByHeightWithOverscan
                        break
                    case "width":
                        scale = scaleByWidthWithOverscan
                        break
                    case "height":
                        scale = scaleByHeightWithOverscan
                        break
                    default:
                        break;
                }
            }
        }

        this.setState({ scale: abs(scale) })
    }

    renderCollageLayoutClipMasks = (scale: number) => {
        const { form, collageLayout } = this.props

        if (!form?.is_collage || !collageLayout || !collageLayout.images || collageLayout.images.length === 0) {
            return <React.Fragment />
        }

        const templateSize = getTemplateSize(form)

        return <React.Fragment>
            {Object.keys(collageLayout.images).map((i) => {
                const image: CollageLayoutImageType = collageLayout.images[i]

                const clipPathId = `collage-clip-path-canvas-${collageLayout.id}-${image.id}`

                const scaleX = collageLayout.rel_width * templateSize.width
                const scaleY = collageLayout.rel_height * templateSize.height

                const width = (scaleX * image.width_rel - 2 * image.padding_px) / scale
                const height = (scaleY * image.height_rel - 2 * image.padding_px) / scale

                let thisWidth = width
                let thisHeight = height

                if (image.shape === 'circle') {
                    const minSide = min(thisWidth, thisHeight)
                    thisWidth = minSide
                    thisHeight = minSide
                }

                const points = image.points ? JSON.parse(image.points) : [] //bezier nebo polygon
                let scaledPoints = [...points]  //relative units to pixels
                scaledPoints.forEach((point, index) => {
                    points[index].x = scaleX * image.width_rel * point.x / scale
                    points[index].y = scaleY * image.height_rel * point.y / scale

                    if (typeof point.cx !== 'undefined') {
                        points[index].cx = scaleX * image.width_rel * point.cx / scale
                    }

                    if (typeof point.cy !== 'undefined') {
                        points[index].cy = scaleY * image.height_rel * point.cy / scale
                    }
                })

                const maskObject: ClipMaskObject = {
                    ...EmptyClipMaskObject,
                    width: thisWidth,
                    height: thisHeight,
                    padding: image.padding_px,
                    /* angle: image.angle, */
                    x: 0,
                    y: 0,
                    isFilled: true,
                    type: image.shape ? image.shape : 'rect',
                    points: scaledPoints,
                }

                return <clipPath key={clipPathId} id={clipPathId} transform={`translate(${width / 2}, ${height / 2})`}>
                    {renderClipMaskShape(maskObject, false)}
                </clipPath>
            })}

        </React.Fragment>
    }

    imageBlobCorrespondsToRequest = (form, randomProduct): boolean => {
        let imageBlobCorrespondsToRequest = randomProduct.imageBlob && (randomProduct.imageBlobType.templateType === form.type)
        if (form.type === RENDERED_TYPE_OUTPAINTING) {
            if (form.outpaint_over && randomProduct.imageBlobType && (form.overscan || form.translate_x || form.translate_y) && (
                randomProduct.imageBlobType.overscan !== form.overscan ||
                randomProduct.imageBlobType.translate_x !== form.translate_x ||
                randomProduct.imageBlobType.translate_y !== form.translate_y
            )) {
                imageBlobCorrespondsToRequest = false
            }
        }

        return imageBlobCorrespondsToRequest
    }

    isOutpaintOver = (imageBlobCorrespondsToRequest: boolean) => {
        const { randomProduct, form } = this.props
        const { outpaint_over, translate_x, translate_y, overscan } = form
        return canBeOutpaintedOver(outpaint_over, overscan, translate_x, translate_y) &&
            form.type === RENDERED_TYPE_OUTPAINTING &&
            randomProduct.imageBlob &&
            imageBlobCorrespondsToRequest
    }

    getImageUrlAndSize = () => {
        const { form, isAlt, randomProduct, levels, levelsMode, isTemplatePreview } = this.props
        const imageBlobCorrespondsToRequest = this.imageBlobCorrespondsToRequest(form, randomProduct)

        const origAlt = getAltImageFromRandomProduct(randomProduct, levels, levelsMode)

        const altImageByLevel = imageBlobCorrespondsToRequest && !isTemplatePreview ? randomProduct.imageBlob :
            (form.type === RENDERED_TYPE_BGREMOVE && randomProduct.bgremovedImage ? randomProduct.bgremovedImage : origAlt)

        const mainImage = imageBlobCorrespondsToRequest && !isTemplatePreview ? randomProduct.imageBlob :
            (form.type === RENDERED_TYPE_BGREMOVE && randomProduct.bgremovedImage ? randomProduct.bgremovedImage : randomProduct.image_link)

        const imageUrl = isAlt ? altImageByLevel : mainImage
        const isOrig = (imageUrl === (isAlt ? origAlt : randomProduct.image_link)) ||
            (isTemplatePreview && randomProduct.bgremovedImage)

        const randomProductWidth = isOrig ? randomProduct.origWidth : randomProduct.width
        const randomProductHeight = isOrig ? randomProduct.origHeight : randomProduct.height

        //console.debug(`get image url and size ${imageUrl}`, randomProduct, isOrig)

        return {
            imageUrl: imageUrl,
            isOrig: isOrig,
            randomProductWidth: randomProductWidth,
            randomProductHeight: randomProductHeight,
        }
    }

    render() {
        const { form, isAlt, randomProduct, collageLayout, isTemplatePreview, transX, transY, levels } = this.props
        const { scale } = this.state

        const { rotation, translate_x, translate_y,  overscan, outpaint_over, flip, flop, alpha, is_collage, is_anim_gif, frames_delay, loops_delay } = form

        const templateSize = getTemplateSize(form)

        const scaleX = (flop === 1 ? -1 : 1) * scale
        const scaleY = (flip === 1 ? -1 : 1) * scale

        const imageBlobCorrespondsToRequest = this.imageBlobCorrespondsToRequest(form,randomProduct)

        const r = this.getImageUrlAndSize()

        const imageWidth = is_collage && collageLayout ? templateSize.width * collageLayout.rel_width / scale : (r.randomProductWidth || 1)
        const imageHeight = is_collage && collageLayout ? templateSize.height * collageLayout.rel_height / scale : (r.randomProductHeight || 1)

        const isShadow = form.shadow_color && form.shadow_sigma
        const className = is_collage && collageLayout ? 'alternative-image-preview' : 'shape draggable-group selected-shape'

        const isOutpaintingOver = this.isOutpaintOver(imageBlobCorrespondsToRequest)

        const xoffset = isOutpaintingOver ? 0 : translate_x
        const yoffset = isOutpaintingOver ? 0 : translate_y

        return (
            <React.Fragment>
                <g transform={`translate(${.5 * templateSize.width + xoffset + transX},${.5 * templateSize.height + yoffset + transY})`}>
                    <defs>
                        {form.has_reflection &&
                            <linearGradient id={`gradient-source-image`} y1="0%" y2="100%">
                                <stop offset="0%" stopColor="black" stopOpacity=".9" />
                                <stop offset="100%" stopColor="white" stopOpacity="1" />
                            </linearGradient>
                        }

                        {form.has_reflection &&
                            <mask id={`gradient-mask-source-image`}>
                                <rect x={0} y={0} width={imageWidth} height={imageHeight} fill={`url(#gradient-source-image)`} />
                            </mask>
                        }

                        {isShadow &&
                            <filter id={`shadow-source-image`} width="200%" height="200%" y="-50%" x="-50%">
                                <feDropShadow
                                    dx={form.shadow_xoffset / scale}
                                    dy={form.shadow_yoffset / scale}
                                    stdDeviation={form.shadow_sigma / scale}
                                    floodColor={form.shadow_color}
                                    floodOpacity={1 - form.shadow_opacity / 100}
                                />
                            </filter>
                        }

                        {this.renderCollageLayoutClipMasks(scale)}
                    </defs>

                    <g className={className} transform={`rotate(${rotation}), scale(${scaleX}, ${scaleY}), translate(${-.5 * imageWidth}, ${-.5 * imageHeight})`}>
                        {is_collage && collageLayout ?
                            <AlternativeImageSvg
                                draggable={false}
                                selectedCollage={collageLayout}
                                selectedImageId={null}
                                randomProduct={randomProduct}
                                layoutWidth={imageWidth}
                                layoutHeight={imageHeight}
                                isShadow={!!isShadow}
                                isAnim={!!is_anim_gif}
                                framesDelay={frames_delay}
                                loopsDelay={loops_delay}
                            />
                            :
                            <g>
                                {form.has_reflection &&
                                    <g transform={`translate(0, ${form.reflection_offset}), scale(1, -1), skewX(${form.reflection_angle}), translate(${round(tan(PI * form.reflection_angle / 180) * imageHeight)}, ${-2 * imageHeight})`} className={'shape-reflection'} mask={`url(#gradient-mask-source-image)`}>
                                        <image
                                            className={`image-preview ${isTemplatePreview ? 'template-preview-reflection' : 'main-image-reflection'}`}
                                            width={imageWidth}
                                            height={imageHeight}
                                            xlinkHref={r.imageUrl}
                                            style={{
                                                opacity: 1 - (form.reflection_opacity || 0) / 100
                                            }}
                                        />
                                    </g>
                                }

                                <image
                                    className={`image-preview ${isTemplatePreview ? 'template-preview-image' : 'main-image-layer'}`}
                                    width={imageWidth}
                                    height={imageHeight}
                                    key={`updateimage_${r.imageUrl?.length}${this.props.randomProduct.bgremovedImage?.length}`}
                                    xlinkHref={r.imageUrl}
                                    preserveAspectRatio={'xMidYMid slice'}
                                    filter={isShadow ? 'url(#shadow-source-image)' : ''}
                                    style={{
                                        opacity: alpha ? alpha : 1
                                    }}
                                />

                            </g>
                        }
                    </g>
                </g>
            </React.Fragment>
        )
    }
}

export default SourceImageSvgElements
